import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { WinModal } from './win-modal';
import { getWord, getGameNumber } from "./utils/get-word";
import { ANSWER_WORDS } from './utils/answer-words';
import { ALLOWED_WORDS } from './utils/allowed-words';
import { toast } from 'react-toastify';

// Constants
const WORD_LENGTH = 5;
const MAX_GUESSES = 6;
const FLIP_STAGGER_MS = 300;
const FLIP_DURATION_MS = 500;
const REVEAL_TOTAL_MS = FLIP_STAGGER_MS * (WORD_LENGTH - 1) + FLIP_DURATION_MS;
const SHAKE_DURATION_MS = 600;

// Wordle-accurate tile evaluation: greens claim their letters first, then
// yellows are awarded left-to-right from whatever letters remain, so
// duplicate letters never light up more tiles than the solution contains.
const evaluateGuess = (guess, solution) => {
  const result = Array(WORD_LENGTH).fill('absent');
  const remaining = {};

  for (let i = 0; i < WORD_LENGTH; i++) {
    if (guess[i] === solution[i]) {
      result[i] = 'correct';
    } else {
      remaining[solution[i]] = (remaining[solution[i]] || 0) + 1;
    }
  }

  for (let i = 0; i < WORD_LENGTH; i++) {
    if (result[i] !== 'correct' && remaining[guess[i]] > 0) {
      result[i] = 'present';
      remaining[guess[i]]--;
    }
  }

  return result;
};

const STATUS_RANK = { unused: 0, absent: 1, present: 2, correct: 3 };

const getLetterStatuses = (guesses, solution) => {
  const statuses = {};
  for (const guess of guesses) {
    const evaluation = evaluateGuess(guess, solution);
    for (let i = 0; i < WORD_LENGTH; i++) {
      const letter = guess[i];
      const current = statuses[letter] || 'unused';
      if (STATUS_RANK[evaluation[i]] > STATUS_RANK[current]) {
        statuses[letter] = evaluation[i];
      }
    }
  }
  return statuses;
};

const WordleKeyboard = ({ onKeyPress, letterStatuses }) => {
  const rows = [
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
    ['ENTER', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'BACKSPACE']
  ];

  return (
    <div className="wordle-keyboard">
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className="keyboard-row">
          {rowIndex === 1 && <div className="keyboard-spacer" />}
          {row.map((key) => {
            const isSpecial = key === 'ENTER' || key === 'BACKSPACE';
            const keyStatus = isSpecial ? 'special' : (letterStatuses[key] || 'unused');

            return (
              <button
                key={key}
                className={`keyboard-key ${keyStatus}${isSpecial ? ' wide' : ''}`}
                onClick={() => onKeyPress(key === 'BACKSPACE' ? 'Backspace' : key)}
              >
                {key === 'BACKSPACE' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                    <path fill="currentColor" d="M22 3H7c-.69 0-1.23.35-1.59.88L0 12l5.41 8.11c.36.53.9.89 1.59.89h15c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-3 12.59L17.59 17 14 13.41 10.41 17 9 15.59 12.59 12 9 8.41 10.41 7 14 10.59 17.59 7 19 8.41 15.41 12 19 15.59z"></path>
                  </svg>
                ) : key}
              </button>
            );
          })}
          {rowIndex === 1 && <div className="keyboard-spacer" />}
        </div>
      ))}
    </div>
  );
};

const STATUS_EMOJI = { correct: '🟩', present: '🟨', absent: '⬛' };

const generateShareString = (guessHistory, solution) => {
  const gameNumber = getGameNumber();
  const emojiGrid = guessHistory
    .map((guess) => evaluateGuess(guess, solution).map((status) => STATUS_EMOJI[status]).join(''))
    .join('\n');

  return `Kirtdle ${gameNumber} ${guessHistory.length}/${MAX_GUESSES}\n\n${emojiGrid}`;
};

const showToast = (message) => {
  toast(message, {
    position: 'top-center',
    hideProgressBar: true,
    closeButton: false,
    pauseOnHover: false,
    autoClose: 1000,
    className: 'game-toast',
  });
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    showToast('Copied results to clipboard');
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    showToast('Failed to copy to clipboard');
  }
};

const CloseButton = ({ onClose }) => (
  <button className="close-button" type="button" onClick={onClose} aria-label="Close">
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
      <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
    </svg>
  </button>
);

const WinFragment = ({ onClose, guessHistory, solution }) => (
  <div className="modal-content">
    <CloseButton onClose={onClose} />
    <h2 className="modal-title">Splendid!</h2>
    <p className="modal-subtitle">Kirtdle {getGameNumber()} — {guessHistory.length}/{MAX_GUESSES}</p>
    <div className="share-container">
      <button className="share-button" onClick={() => copyToClipboard(generateShareString(guessHistory, solution))}>
        Share
        <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20">
          <path fill="currentColor" d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"></path>
        </svg>
      </button>
    </div>
  </div>
);

const LoseFragment = ({ onClose, solution }) => (
  <div className="modal-content">
    <CloseButton onClose={onClose} />
    <h2 className="modal-title">Better luck tomorrow</h2>
    <p className="modal-subtitle">The word was <strong>{solution.toUpperCase()}</strong></p>
    <p className="modal-subtitle">Kirtdle {getGameNumber()}</p>
  </div>
);

// Main component
export const GuessRow = () => {
  const [guessHistory, setGuessHistory] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameState, setGameState] = useState('playing'); // 'playing', 'won', 'lost'
  const [showModal, setShowModal] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const timersRef = useRef([]);

  const solution = useMemo(() => getWord(), []);

  // Clear any pending timers on unmount
  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  const setTimer = (fn, ms) => timersRef.current.push(setTimeout(fn, ms));

  const shakeRow = useCallback((message) => {
    showToast(message);
    setIsShaking(true);
    setTimer(() => setIsShaking(false), SHAKE_DURATION_MS);
  }, []);

  const submitGuess = useCallback(() => {
    if (currentGuess.length !== WORD_LENGTH) {
      shakeRow('Not enough letters');
      return;
    }

    if (!ALLOWED_WORDS.includes(currentGuess) && !ANSWER_WORDS.includes(currentGuess)) {
      shakeRow('Not in word list');
      return;
    }

    const newGuessHistory = [...guessHistory, currentGuess];
    setGuessHistory(newGuessHistory);
    setCurrentGuess('');
    setIsRevealing(true);

    const won = currentGuess === solution;
    const lost = !won && newGuessHistory.length >= MAX_GUESSES;

    setTimer(() => {
      setIsRevealing(false);
      if (won) {
        setGameState('won');
        setTimer(() => setShowModal(true), 1200); // let the bounce play first
      } else if (lost) {
        setGameState('lost');
        setTimer(() => setShowModal(true), 300);
      }
    }, REVEAL_TOTAL_MS);
  }, [currentGuess, guessHistory, solution, shakeRow]);

  const handleKeyPress = useCallback((key) => {
    if (gameState !== 'playing' || isRevealing) return;

    if (key === 'Enter' || key === 'ENTER') {
      submitGuess();
    } else if (key === 'Backspace') {
      setCurrentGuess((guess) => guess.slice(0, -1));
    } else if (key.match(/^[a-zA-Z]$/)) {
      setCurrentGuess((guess) => (guess.length < WORD_LENGTH ? guess + key.toLowerCase() : guess));
    }
  }, [gameState, isRevealing, submitGuess]);

  // Physical keyboard listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      handleKeyPress(e.key);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyPress]);

  // Keyboard colors only update once the reveal animation has finished
  const letterStatuses = useMemo(() => {
    const revealed = isRevealing ? guessHistory.slice(0, -1) : guessHistory;
    return getLetterStatuses(revealed, solution);
  }, [guessHistory, isRevealing, solution]);

  const renderCompletedRow = (guess, rowIndex) => {
    const evaluation = evaluateGuess(guess, solution);
    const isWinningRow = gameState === 'won' && rowIndex === guessHistory.length - 1;

    return (
      <div className="row" key={rowIndex}>
        {guess.split('').map((letter, i) => (
          <div
            key={i}
            className={`tile ${evaluation[i]} ${isWinningRow ? 'bounce' : 'reveal'}`}
            style={{ animationDelay: isWinningRow ? `${i * 100}ms` : `${i * FLIP_STAGGER_MS}ms` }}
          >
            {letter}
          </div>
        ))}
      </div>
    );
  };

  const renderCurrentRow = (rowIndex) => (
    <div className={`row${isShaking ? ' shake' : ''}`} key={rowIndex}>
      {Array.from({ length: WORD_LENGTH }, (_, i) => (
        <div key={i} className={`tile${currentGuess[i] ? ' filled' : ''}`}>
          {currentGuess[i] || ''}
        </div>
      ))}
    </div>
  );

  const renderEmptyRow = (rowIndex) => (
    <div className="row" key={rowIndex}>
      {Array.from({ length: WORD_LENGTH }, (_, i) => (
        <div key={i} className="tile" />
      ))}
    </div>
  );

  const rows = Array.from({ length: MAX_GUESSES }, (_, i) => {
    if (i < guessHistory.length) return renderCompletedRow(guessHistory[i], i);
    if (i === guessHistory.length && gameState === 'playing') return renderCurrentRow(i);
    return renderEmptyRow(i);
  });

  return (
    <div className="game-container">
      <div className="board">{rows}</div>

      <WinModal show={showModal} onClose={() => setShowModal(false)}>
        {gameState === 'won' ? (
          <WinFragment onClose={() => setShowModal(false)} guessHistory={guessHistory} solution={solution} />
        ) : (
          <LoseFragment onClose={() => setShowModal(false)} solution={solution} />
        )}
      </WinModal>

      <WordleKeyboard onKeyPress={handleKeyPress} letterStatuses={letterStatuses} />
    </div>
  );
};
